import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import api, { formatApiError, getWsUrl } from "@/lib/api";
import logger from "@/lib/logger";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import AnnotationModal from "@/components/AnnotationModal";
import { ChatDateDivider, sameDay } from "@/lib/chatTime";
import { useChatTyping, TypingLine } from "@/lib/chatTyping";
import { ChatMessage, renderRich } from "@/pages/Chat";
import RichTextInput from "@/components/RichTextInput";
import { FormatToolbar, ShortcodeMenu, detectShortcode, searchShortcodes, replaceShortcode, hasFormatting, handleFormatShortcut } from "@/components/ChatComposerKit";
import { MessageSquare, X, Pin, Paperclip, Send, Loader2, FileText, ChevronRight } from "lucide-react";
import { toast } from "sonner";

/**
 * Feb 2026 — Information Cleanse: the Project-Centric Sidecar.
 * All project-scoped chatter now lives HERE (not in the global Chat tab).
 * Backed by the same project channel _ensure_project_channel() already
 * auto-syncs (team + client manager + client + admins), fetched lazily via
 * GET /projects/{id}/sidecar-channel on first open. Reuses <ChatMessage>
 * from the main Chat page (pin / annotate / quote-reply all work exactly
 * the same way) plus a new "Push to Project Update" flow that lets you
 * edit/summarize the text before it goes to the Client Portal.
 */
export default function ProjectChatSidecar({ projectId }) {
  const { user } = useAuth();
  const [pinned, setPinned] = useState(() => {
    try { return window.localStorage.getItem(`sidecar-pinned-${projectId}`) === "1"; } catch { return false; }
  });
  const [open, setOpen] = useState(pinned);
  const [channelId, setChannelId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [annotateTarget, setAnnotateTarget] = useState(null);
  const [pushTarget, setPushTarget] = useState(null); // message being pushed to Project Update
  const [pushSummary, setPushSummary] = useState("");
  const [pushing, setPushing] = useState(false);
  const [shortcodeQuery, setShortcodeQuery] = useState(null);
  const [shortcodeIndex, setShortcodeIndex] = useState(0);
  const listRef = useRef(null);
  const fileInputRef = useRef(null);
  const inputRef = useRef(null);
  const richRef = useRef(null);
  const wsRef = useRef(null);
  const { typingUsers, setChannel: setTypingChannel, handleWsMsg: handleTypingWs, clearSender, sendTyping } = useChatTyping(wsRef, user?.id);
  const shortcodeItems = useMemo(() => searchShortcodes(shortcodeQuery || "", 8), [shortcodeQuery]);
  const insertShortcode = (e) => {
    const el = inputRef.current;
    const caret = el && typeof el.selectionStart === "number" ? el.selectionStart : text.length;
    const { next, caret: newCaret } = replaceShortcode(text, caret, e.emoji);
    setText(next);
    setShortcodeQuery(null); setShortcodeIndex(0);
    setTimeout(() => { const n = inputRef.current; if (n) { n.focus(); try { n.setSelectionRange(newCaret, newCaret); } catch (err) { /* noop */ } } }, 0);
  };
  const copyLink = (msg) => {
    const link = `${window.location.origin}/projects/${projectId}?chatmsg=${msg.id}`;
    const done = () => toast.success("Message link copied");
    try {
      if (navigator.clipboard?.writeText) navigator.clipboard.writeText(link).then(done).catch(() => toast.error("Copy failed"));
      else { const ta = document.createElement("textarea"); ta.value = link; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta); done(); }
    } catch (err) { toast.error("Copy failed"); }
  };

  const togglePin = () => {
    const next = !pinned;
    setPinned(next);
    try { window.localStorage.setItem(`sidecar-pinned-${projectId}`, next ? "1" : "0"); } catch (err) { logger.debug("localStorage write failed:", err?.message || err); }
    if (next) setOpen(true);
  };

  // Deep-link: /projects/<id>?chatmsg=<msgId> opens the sidecar and flags the
  // message to scroll-to + flash once it loads.
  const [deepLinkMsg, setDeepLinkMsg] = useState(null);
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const mid = sp.get("chatmsg");
    if (mid) {
      setDeepLinkMsg(mid);
      setOpen(true);
      try { window.history.replaceState({}, "", `/projects/${projectId}`); } catch (e) { /* noop */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!deepLinkMsg || !open) return undefined;
    const el = document.getElementById(`msg-${deepLinkMsg}`);
    if (!el) return undefined;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("ring-2", "ring-[#F54800]", "rounded-sm");
    const tm = setTimeout(() => {
      el.classList.remove("ring-2", "ring-[#F54800]", "rounded-sm");
      setDeepLinkMsg(null);
    }, 2200);
    return () => clearTimeout(tm);
  }, [messages, deepLinkMsg, open]);

  const loadMessages = useCallback((cid) => {
    api.get("/chat/messages", { params: { channel_id: cid } })
      .then(({ data }) => {
        setMessages(data);
        setTimeout(() => listRef.current?.scrollTo(0, listRef.current.scrollHeight), 50);
      })
      .catch((err) => toast.error(formatApiError(err)));
  }, []);

  // Fetch/lazily-create the project's sidecar channel as soon as the
  // component mounts (every ProjectDetail page load) — not gated behind
  // `open`, so opening the drawer is instant instead of racing a fetch.
  useEffect(() => {
    if (!projectId) return;
    api.get(`/projects/${projectId}/sidecar-channel`)
      .then(({ data }) => { setChannelId(data.channel_id); loadMessages(data.channel_id); })
      .catch((err) => toast.error(formatApiError(err)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Scoped websocket — only messages for THIS project's channel are kept.
  useEffect(() => {
    if (!channelId) return;
    setTypingChannel(channelId);
    const ws = new WebSocket(getWsUrl());
    wsRef.current = ws;
    ws.onmessage = (ev) => {
      let msg;
      try { msg = JSON.parse(ev.data); } catch { return; }
      if (msg.type === "typing") { handleTypingWs(msg); return; }
      if (msg.channel_id !== channelId) return;
      clearSender(msg.sender_id);
      setMessages((m) => {
        const idx = m.findIndex((x) => x.id === msg.id);
        if (idx >= 0) { const next = [...m]; next[idx] = msg; return next; }
        setTimeout(() => listRef.current?.scrollTo(0, listRef.current.scrollHeight), 50);
        return [...m, msg];
      });
    };
    ws.onerror = (err) => logger.warn("Sidecar WS error:", err);
    return () => { try { ws.close(); } catch (err) { logger.debug("Sidecar WS close failed:", err); } };
  }, [channelId, setTypingChannel, handleTypingWs, clearSender]);

  const send = async () => {
    const md = richRef.current ? richRef.current.getMarkdown() : "";
    const mentionIds = richRef.current ? richRef.current.getMentionIds() : [];
    if ((!md.trim() && !attachment) || !channelId) return;
    try {
      await api.post("/chat/messages", {
        channel_id: channelId,
        text: md,
        mentions: mentionIds,
        attachments: attachment ? [attachment] : undefined,
        reply_to_id: replyTo?.id || undefined,
      });
      richRef.current?.clear(); setAttachment(null); setReplyTo(null);
      sendTyping(false);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const pickFile = () => fileInputRef.current?.click();
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) uploadOne(file);
  };
  const uploadOne = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Max file size is 10 MB"); return; }
    const isImage = file.type.startsWith("image/");
    if (!isImage && file.type !== "application/pdf") { toast.error("Only images or PDFs can be attached"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAttachment({ url: data.path, name: file.name, type: isImage ? "image" : "pdf", size: data.size });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const handleAnnotationSaved = async (att) => {
    if (!channelId || !annotateTarget) return;
    try {
      await api.post("/chat/messages", {
        channel_id: channelId,
        text: "📐 Annotated feedback",
        attachments: [att],
        annotation_of: annotateTarget.msg.id,
      });
    } catch (err) { toast.error(formatApiError(err)); }
    setAnnotateTarget(null);
  };

  const pinMessage = async (msg) => {
    try { await api.post(`/chat/messages/${msg.id}/pin`); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const openPushDialog = (msg) => {
    if (msg.client_shared) {
      // Retract — no summary needed.
      api.post(`/chat/messages/${msg.id}/share-to-client`)
        .then(() => toast.success("Retracted from client view"))
        .catch((err) => toast.error(formatApiError(err)));
      return;
    }
    setPushTarget(msg);
    setPushSummary(msg.text || "");
  };
  const confirmPush = async () => {
    if (!pushTarget) return;
    setPushing(true);
    try {
      await api.post(`/chat/messages/${pushTarget.id}/share-to-client`, { summary: pushSummary });
      toast.success("Pushed to the Client Portal's Project Updates");
      setPushTarget(null);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setPushing(false); }
  };

  const messagesById = Object.fromEntries(messages.map((m) => [m.id, m]));

  // Feb 2026 — rendered via a portal straight into document.body. The
  // ProjectDetail page wraps everything in a `.fade-in` div, and CSS
  // `transform` on ANY ancestor (even a completed `translateY(0)`
  // animation) creates a new containing block for `position: fixed`
  // descendants — silently turning "fixed to the viewport" into "fixed to
  // that div" instead, which drifted out of place while scrolling. A
  // portal sidesteps the whole component tree so the drawer + trigger are
  // always fixed to the real viewport, no matter what wraps this component.
  return createPortal(
    <>
      {/* Collapsed handle — floating tab fixed centre-right of the viewport */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="fixed right-0 top-1/2 -translate-y-1/2 z-40 bg-slate-900 hover:bg-[#F54800] text-white rounded-l-md px-2 py-4 shadow-lg transition-colors flex flex-col items-center gap-1.5"
          data-testid="sidecar-open-btn"
          title="Open project chat"
        >
          <MessageSquare size={16} />
          <span className="text-[10px] font-semibold tracking-wide [writing-mode:vertical-rl]">Chat</span>
        </button>
      )}

      {/* Drawer — fixed to the viewport, full height, own internal scroll */}
      <div
        className={`fixed top-0 right-0 h-screen w-[360px] max-w-[92vw] bg-white border-l border-slate-200 shadow-2xl z-40 flex flex-col transition-transform duration-300 ease-out ${open ? "translate-x-0" : "translate-x-full"}`}
        data-testid="project-chat-sidecar"
      >
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-900 text-white">
          <div className="flex items-center gap-2 min-w-0">
            <MessageSquare size={15} />
            <span className="font-display text-sm font-bold truncate">Project Chat</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={togglePin}
              title={pinned ? "Unpin (auto-collapse)" : "Pin panel open"}
              className={`p-1.5 rounded-sm ${pinned ? "text-[#F54800] bg-white/10" : "text-white/60 hover:text-white hover:bg-white/10"}`}
              data-testid="sidecar-pin-btn"
            >
              <Pin size={14} fill={pinned ? "currentColor" : "none"} />
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              title="Collapse"
              className="p-1.5 rounded-sm text-white/60 hover:text-white hover:bg-white/10"
              data-testid="sidecar-close-btn"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>

        <div ref={listRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-2 bg-[#F8F9FA]" data-testid="sidecar-messages">
          {messages.map((m, i) => (
            <React.Fragment key={m.id}>
              {(i === 0 || !sameDay(messages[i - 1].created_at, m.created_at)) && (
                <ChatDateDivider date={m.created_at} messages={messages} />
              )}
              <ChatMessage
                msg={m}
                mine={m.sender_id === user.id}
                currentUserId={user.id}
                quoted={m.reply_to_id ? messagesById[m.reply_to_id] : null}
                canShare
                onAnnotate={(msg, att) => setAnnotateTarget({ msg, att })}
                onPin={pinMessage}
                onPushUpdate={openPushDialog}
                onReply={setReplyTo}
                onCopyLink={copyLink}
              />
            </React.Fragment>
          ))}
          {messages.length === 0 && channelId && (
            <div className="text-center text-xs text-slate-400 mt-10">No project chat yet. Kick things off 👋</div>
          )}
          {!channelId && (
            <div className="text-center text-xs text-slate-400 mt-10 flex items-center justify-center gap-1.5"><Loader2 size={13} className="animate-spin" /> Loading…</div>
          )}
        </div>

        <TypingLine typingUsers={typingUsers} testId="sidecar-typing-indicator" className="px-3 pb-0.5 pt-0.5" />
        <div className="bg-white border-t border-slate-200">
          {replyTo && (
            <div className="px-3 pt-2 flex items-center justify-between gap-2 text-xs text-slate-500 border-l-2 border-[#F54800] mx-3 mt-2" data-testid="sidecar-reply-preview">
              <span className="truncate">Replying to <b>{replyTo.sender_name}</b>: {replyTo.text || "📎 attachment"}</span>
              <button type="button" onClick={() => setReplyTo(null)} className="text-slate-400 hover:text-slate-700 shrink-0"><X size={12} /></button>
            </div>
          )}
          {attachment && (
            <div className="px-3 pt-2 flex items-center gap-2" data-testid="sidecar-attachment-preview">
              <span className="inline-flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-sm px-2 py-1 text-xs text-slate-700">
                {attachment.type === "image" ? <img src={attachment.url} alt="" className="w-6 h-6 rounded-sm object-cover" /> : <FileText size={13} className="text-[#F54800]" />}
                {attachment.name}
                <button type="button" onClick={() => setAttachment(null)} className="text-slate-400 hover:text-rose-600"><X size={12} /></button>
              </span>
            </div>
          )}
          <div className="px-3 py-3 flex items-end gap-1.5 relative">
            <input ref={fileInputRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={handleFileSelect} data-testid="sidecar-file-input" />
            <button
              type="button"
              onClick={pickFile}
              disabled={uploading || !channelId}
              title="Attach image or PDF"
              className="p-2 rounded-sm text-slate-400 hover:text-[#F54800] hover:bg-slate-50 shrink-0 self-end pb-1 disabled:opacity-40"
              data-testid="sidecar-attach-btn"
            >
              {uploading ? <Loader2 size={15} className="animate-spin" /> : <Paperclip size={15} />}
            </button>
            <div className="flex-1 min-w-0">
              <RichTextInput
                ref={richRef}
                size="xs"
                users={[]}
                onSubmit={send}
                onTyping={sendTyping}
                onPasteFiles={(files) => files[0] && uploadOne(files[0])}
                placeholder="Message the project team…"
                disabled={!channelId}
                testIdPrefix="sidecar"
                inputTestId="sidecar-chat-input"
              />
            </div>
            <Button onClick={send} disabled={!channelId} className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white shrink-0 self-end" data-testid="sidecar-send-message"><Send size={13} /></Button>
          </div>
        </div>
      </div>

      <AnnotationModal
        open={!!annotateTarget}
        onClose={() => setAnnotateTarget(null)}
        imageUrl={annotateTarget?.att?.url}
        imageName={annotateTarget?.att?.name}
        onSaved={handleAnnotationSaved}
      />

      <Dialog open={!!pushTarget} onOpenChange={(v) => !v && setPushTarget(null)}>
        <DialogContent className="rounded-sm max-w-[calc(100vw-2rem)] sm:max-w-md" data-testid="push-update-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Push to Project Update</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Summarize this before it goes out to the client — they'll see it in Project Updates &amp; their chat.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            rows={4}
            value={pushSummary}
            onChange={(e) => setPushSummary(e.target.value)}
            className="rounded-sm text-sm resize-none"
            placeholder="Client-facing summary…"
            data-testid="push-update-summary"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setPushTarget(null)} className="rounded-sm">Cancel</Button>
            <Button onClick={confirmPush} disabled={pushing} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="push-update-confirm">
              {pushing ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <Send size={13} className="mr-1.5" />} Push
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>,
    document.body
  );
}
